import React, { useCallback } from 'react'
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Stack,
  TextField,
} from '@mui/material'
import Tree from '../tree/Tree'
import useVariableTree from '../../hooks/useVariableTree'
import useDataExplorer from '../../hooks/useDataExplorer'
import { CarbonInterviewRawDataRow } from '../../types/gotypes'
import { GenNode } from '../../types/carbon/models/GenNode'

interface DataExplorerProps {
  customer: string
  job: string
  initialVariableName?: string
  initialFolderPath?: string
  readOnly?: boolean
  onVariableChange: (variable: GenNode) => void
  onDataChange: (data: CarbonInterviewRawDataRow[], totalCount: number) => void
}

// Add React.PropsWithChildren to DataExplorerProps to allow children prop
const DataExplorer: React.FC<React.PropsWithChildren<DataExplorerProps>> = ({
  customer,
  job,
  initialVariableName,
  initialFolderPath,
  readOnly = false,
  onVariableChange,
  onDataChange,
  children,
}) => {

  const dataExplorer = useDataExplorer({
    customer,
    job,
    onDataChange,
  })

  const handleSelectedVariablesChange = useCallback((selectedVariables: GenNode[], nodeMap: Record<string, GenNode>) => {
    if(!selectedVariables || selectedVariables.length === 0) return
    const variable = selectedVariables[0]
    const varName = selectedVariables[0]?.value1 || ''
    dataExplorer.updateAndRunQuery({
      variable: varName,
      filter: `${varName}(*)`,
    })
    onVariableChange(variable)
  }, [dataExplorer.updateAndRunQuery, onVariableChange])

  const variableTree = useVariableTree({
    customer: customer || '',
    job: job || '',
    autoExpandVariables: false,
    loadCodes: false,
    singleVariable: true,
    autoCheckOnSelect: true,
    initialVariableNames: initialVariableName ? [initialVariableName] : [],
    initialFolderPath: initialFolderPath,
    onSelectedVariablesChange: handleSelectedVariablesChange,
  })

  const hasTreeSelection = variableTree.selectedVariables.length > 0

  const sidebar = variableTree.isLoading ? (
    <Box sx={{ p: 2 }}>
      <Typography variant='body2' color='text.secondary'>Loading variable tree...</Typography>
    </Box>
  ) : (
    <Tree
      searchEnabled
      searchPlaceholder='Search variables...'
      showTooltips
      disabled={readOnly}
      nodes={variableTree.nodes || []}
      expanded={variableTree.expanded}
      getCheckboxState={variableTree.getCheckboxState}
      onNodeExpandToggle={variableTree.onNodeExpandToggle}
      onNodeCheckedToggle={variableTree.onNodeCheckedToggle}
      onNodeSelectToggle={variableTree.onNodeSelectToggle}
    />
  )

  const tableData = (() => {
    const fields: string[] = []
    const rows = dataExplorer.data.map(row => {
      const values = row.data.reduce<Record<string, string>>((acc, item) => {
        if (!fields.includes(item.variable_name)) {
          fields.push(item.variable_name)
        }
        acc[item.variable_name] = (acc[item.variable_name] || '') + item.data
        return acc
      }, {})

      return {
        interviewId: row.interview_id,
        values,
        raw: row,
      }
    })
    return {
      fields,
      rows,
    }
  })()

  const renderTableBody = () => {
    if (dataExplorer.isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={2} align='center'>
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      )
    }

    if (dataExplorer.error) {
      return (
        <TableRow>
          <TableCell colSpan={2}>
            <Alert severity='error'>{dataExplorer.error.message}</Alert>
          </TableCell>
        </TableRow>
      )
    }

    if (!dataExplorer.data.length) {
      return (
        <TableRow>
          <TableCell colSpan={2} align='center'>
            <Typography variant='body2' color='text.secondary'>Select a variable from the tree to see data here.</Typography>
          </TableCell>
        </TableRow>
      )
    }

    return tableData.rows.map(row => {
      return (
        <TableRow key={row.interviewId} hover>
          <TableCell width='160px'>{row.interviewId}</TableCell>
          {
            tableData.fields.map(varName => (
              <TableCell key={varName}>{row.values[varName] || ''}</TableCell>
            ))
          }
        </TableRow>
      )
    })
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, height: '100%', minHeight: 0 }}>

      {/* Hide variable tree when in read-only mode (data already confirmed) */}
      {!readOnly && (
        <Paper elevation={1} sx={{ p: 1, maxWidth: 500, width: '30%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {sidebar}
          </Box>
        </Paper>
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, minHeight: 0 }}>
        <Paper elevation={1} sx={{ p: 1 }}>
          <Stack direction='row' spacing={2} alignItems='center'>
            <TextField
              value={dataExplorer.variable}
              onChange={event => dataExplorer.setVariable(event.target.value)}
              onBlur={() => dataExplorer.updateAndRunQuery()}
              label='Variable'
              disabled
              placeholder='Select a variable from the tree'
              size='small'
              fullWidth
              // Read-only affordance: the field is disabled, so a test asserts
              // the tree click landed by reading this value, never by typing
              // into it.
              inputProps={{ 'data-testid': 'data-explorer-variable' }}
            />

            <TextField
              value={dataExplorer.filter}
              onChange={event => dataExplorer.setFilter(event.target.value)}
              onBlur={() => dataExplorer.updateAndRunQuery()}
              label='Filter'
              disabled
              placeholder='Optionally refine the data'
              size='small'
              fullWidth
            />

          </Stack>
        </Paper>

        <Paper elevation={1} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <TableContainer sx={{ flex: 1, minHeight: 0 }}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width='160px'>ID</TableCell>
                  {
                    tableData.fields.map(field => (
                      <TableCell
                        key={field}
                        sx={{ width: `${100 / (tableData.fields.length)}%` }}
                      >
                        {field}
                      </TableCell>
                    ))
                  }
                </TableRow>
              </TableHead>
              <TableBody>
                {renderTableBody()}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component='div'
            count={dataExplorer.totalRows}
            page={dataExplorer.page}
            rowsPerPage={dataExplorer.rowsPerPage}
            onPageChange={(_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
              dataExplorer.updateAndRunQuery({
                page: newPage,
              })
            }}
            onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              dataExplorer.updateAndRunQuery({
                rowsPerPage: Number(event.target.value),
              })
            }}
            rowsPerPageOptions={[10, 20, 50, 100, 500]}
          />
        </Paper>
      </Box>

      {
        children
      }
       
    </Box>
  )
}

export default DataExplorer
